import { Injectable } from '@angular/core';
import {
  catchError,
  EMPTY,
  first,
  map,
  merge,
  Observable,
  ReplaySubject,
  switchMap,
} from 'rxjs';
import {
  CostCenterSummary,
  Identity,
  ProjectSubtaskSummary,
} from '@libs/core/api';

import { LocalStorageService } from './storage.service';
import { NgxAuthService } from 'ngx-auth-utils';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  private readonly localStorageKey = 'teleconsulting-stp--cost-center';

  private readonly user$: Observable<Identity> = this.authService.state.pipe(
    first()
  );

  private readonly initialProject$ = this.user$.pipe(
    map((user) => this.getInitialProject(user, this.readStoredProjectId())),
    catchError(() => EMPTY)
  );

  private readonly latestProject$ = new ReplaySubject<CostCenterSummary>(1);

  /**
   * When no cost-center is selected, this throws an error.
   */
  public readonly selectedProject$: Observable<CostCenterSummary> = merge(
    this.initialProject$,
    this.latestProject$.asObservable()
  );

  constructor(
    private authService: NgxAuthService,
    private localStorageService: LocalStorageService
  ) {}

  selectProject(project: CostCenterSummary): void {
    this.latestProject$.next(project);
    this.storeProjectId(project);
  }

  getInitialProject(
    user: Identity | undefined,
    projectId: number | undefined
  ): CostCenterSummary {
    if (!user) {
      // Transient auth state (e.g. biometric cancel) — do NOT clear the stored
      // project ID so it is still available when biometric succeeds on the next open.
      throw new Error('invalid-user-or-costcenter');
    }

    if (!projectId) {
      this.clearStoredProjectId();
      throw new Error('invalid-user-or-costcenter');
    }

    const company = user.companies?.find((c) =>
      c.costCenters?.some((p) => p.id === projectId)
    );

    const project = company?.costCenters?.find((p) => p.id === projectId);

    if (!project) {
      this.clearStoredProjectId();
      throw new Error('initial-costcenter-not-found');
    }

    return project;
  }

  isBuyerInSomeProject(): Observable<boolean> {
    return this.checkUserSubtaskRole(null, (subtask) => subtask.buyer ?? false);
  }

  isBuyerInSelectedProject(): Observable<boolean> {
    return this.selectedProject$.pipe(
      switchMap((project) =>
        this.checkUserSubtaskRole(project, (subtask) => subtask.buyer ?? false)
      )
    );
  }

  isSuperBuyerInSomeProject(): Observable<boolean> {
    return this.checkUserSubtaskRole(
      null,
      (subtask) => subtask.superBuyer ?? false
    );
  }

  isSuperBuyerInSelectedProject(): Observable<boolean> {
    return this.selectedProject$.pipe(
      switchMap((project) =>
        this.checkUserSubtaskRole(
          project,
          (subtask) => subtask.superBuyer ?? false
        )
      )
    );
  }

  isSuperBuyerInSubtask(subtaskId: number): Observable<boolean> {
    return this.selectedProject$.pipe(
      switchMap((project) =>
        this.checkUserSubtaskRole(
          project,
          (subtask) => (subtask.superBuyer ?? false) && subtask.id === subtaskId
        )
      )
    );
  }

  private storeProjectId(project: CostCenterSummary) {
    this.localStorageService.setItem(this.localStorageKey, `${project.id}`);
  }

  private clearStoredProjectId() {
    this.localStorageService.removeItem(this.localStorageKey);
  }

  private readStoredProjectId(): number | undefined {
    const value = this.localStorageService.getItem(this.localStorageKey);
    return value == null ? undefined : Number(value);
  }

  private checkUserSubtaskRole(
    project: CostCenterSummary | null,
    roleCheck: (subtask: ProjectSubtaskSummary) => boolean
  ): Observable<boolean> {
    if (project) {
      return this.user$.pipe(
        map((user: Identity) => {
          if (!user || !user.companies) {
            return false;
          }
          return user.companies.some((c) =>
            c.costCenters?.some(
              (cc) => cc.id === project.id && cc.subtasks?.some(roleCheck)
            )
          );
        }),
        catchError(() => EMPTY)
      );
    }

    return this.user$.pipe(
      map((user: Identity) => {
        if (!user || !user.companies) {
          return false;
        }
        return user.companies.some((company) =>
          company.costCenters?.some((costCenter) =>
            costCenter.subtasks?.some(roleCheck)
          )
        );
      }),
      catchError(() => EMPTY)
    );
  }
}
